import { Alert, Avatar, Button } from "@mui/material"
import classNames from "classnames"
import ExternalLinks from "components/link"
import Markdown from "markdown-to-jsx"
import PropTypes from "prop-types"
import { MESSAGE_ROLES } from "../../helpers/constants"
import { RefreshableError, RetryableError } from "../../helpers/errors"
import { MESSAGE } from "../../helpers/shapes"
import { useAiTeammateContext, useMessagesContext } from "../../hooks/context"
import AssistantIcon from "../icons/assistant"
import UserIcon from "../icons/user"
import PlotArtifact from "../plot-artifact"
import TableArtifact from "../table-artifact"
import MessageTime from "./message-time"
import "./style.scss"

function AiTeammateMessage({ message, messageTimeRefresher }) {
  const { role, text, createdAt, error, artifacts, code } = message || {}
  const { setError, currency } = useAiTeammateContext()
  const { refetch, dispatchUserMessageInputOverride, lastUserMessageText } = useMessagesContext()

  if (!message) {
    return null
  }

  const icon = role === MESSAGE_ROLES.USER ? <UserIcon /> : <AssistantIcon />

  function handleRetry() {
    dispatchUserMessageInputOverride(lastUserMessageText)
  }

  function handleRefresh() {
    if (lastUserMessageText) {
      dispatchUserMessageInputOverride(lastUserMessageText)
    }
    setError(null)
    refetch()
  }

  let content = <div className="message-container">
    <div className="message-content">
      <ExternalLinks>
        <Markdown>{text || ""}</Markdown>
      </ExternalLinks>

      {code && <span className="message-code">[{code}]</span>}

      {artifacts && artifacts.map(artifact => {
        switch (artifact.type) {
          case "table":
            return <TableArtifact key={artifact.id} artifact={artifact} currency={currency} />
          case "plot":
            return <PlotArtifact key={artifact.id} artifact={artifact} />
          default:
            return null
        }
      })}
    </div>
    {createdAt && <MessageTime date={createdAt} messageTimeRefresher={messageTimeRefresher} />}
  </div>

  if (error) {
    let action
    if (error instanceof RetryableError) {
      action = <Button onClick={handleRetry}>Retry</Button>
    } else if (error instanceof RefreshableError) {
      action = <Button onClick={handleRefresh}>Refresh</Button>
    }

    content = <Alert severity="error" className="error-message" action={action}>
      {error.message}
    </Alert>
  }

  return <>
    <div className={classNames("message", role, { error: !!error })}>
      {content}
      <Avatar className="message-avatar">
        {icon}
      </Avatar>
    </div>
  </>
}

AiTeammateMessage.propTypes = {
  message: MESSAGE,
  messageTimeRefresher: PropTypes.number,
}

export default AiTeammateMessage
